import { Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect, useState } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '~/App/hooks';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import PageLinkBar from '~/components/ui-parts/PageLinkBar';
import { ROUTES } from '~/constants/routes';
import { getSummaryExcelData, getSummaryJsonData } from '~/apiTs';
import {
  SummaryJsonDataResponse,
  SummaryQuestionData,
  SummaryExcelDataResponse,
} from '~/common/types/api/users';
import { GMOASK_STATUS } from '~/constants/api';
import { QUESTION_TYPE_ID, SUMMARY_DATA_TYPE, SummaryDataType } from '~/common/constants';
import SingleAnswer from './SingleAnswer/index';
import NumAnswer from './NumAnswer/index';
import MultipleAnswer from './MultipleAnswer/index';

const PageContent = styled.div`
  width: 960px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: left;
  width: 100%;
  text-align: left;
  flex-direction: column;
`;

const Title = styled(Typography)`
  font-size: ${FONT_SIZES.body1};
`;

const InfoDownloadBox = styled.div`
  display: flex;
  margin-top: 30px;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledButton = styled(Button)`
  border-radius: 20px;
  border: 1px solid ${PALETTE.border.frame};
  background: '#FFFFFF';
  color: ${PALETTE.text.sub};
  font-size: 14px;
  padding: 8px 24px;
`;

const ContentBox = styled.div`
  width: 100%;
  padding: 25px;
  margin: 20px 0;
  border: 1px solid ${PALETTE.border.frame};
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WebAggregation: React.FC<Record<string, never>> = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const survey = useAppSelector((state) => state.survey);
  const surveyId = survey.id
    ? survey.id
    : location.pathname.slice(location.pathname.lastIndexOf('/') + 1);

  const links = [
    {
      label: t('appCommon.enqueteList'),
      routePath: ROUTES.USER.MYPAGE,
    },
    {
      label: `${t('myPage.surveyId')} ${surveyId} ${survey?.name}`,
      routePath: '',
    },
  ];

  const [questionsList, setQuestionsList] = useState<Array<SummaryQuestionData> | []>([]);
  const [isTabulationExecuted, setIsTabulationExecuted] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const { payload: getResponse } = (await dispatch(
        getSummaryJsonData({
          surveyId: Number(surveyId),
        })
      )) as unknown as PayloadAction<
        SummaryJsonDataResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      if (getResponse.status === GMOASK_STATUS.OK) {
        setQuestionsList(getResponse.result);
        setIsTabulationExecuted(true);
      } else if (getResponse.status === GMOASK_STATUS.TABULATION_NOT_EXECUTED) {
        setIsTabulationExecuted(false);
      }
    };
    init();
  }, []);

  const base64toExcelFile = (base64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onClickDownload = async (dataSummaryType: SummaryDataType) => {
    const { payload: getResponse } = (await dispatch(
      getSummaryExcelData({
        surveyId: Number(surveyId),
        summaryType: dataSummaryType,
      })
    )) as unknown as PayloadAction<
      SummaryExcelDataResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (getResponse.status === GMOASK_STATUS.OK) {
      base64toExcelFile(getResponse.result.data, getResponse.result.filename);
    }
  };

  const onClickGtDownload = () => {
    onClickDownload(SUMMARY_DATA_TYPE.GT_TABLE);
  };
  const onClickDataMapDownload = () => {
    onClickDownload(SUMMARY_DATA_TYPE.DATA_MAP_RAW);
  };
  const onClickLabelOutputDownload = () => {
    onClickDownload(SUMMARY_DATA_TYPE.LABEL_EXPORT);
  };

  return (
    <PageContent>
      {links !== null && <PageLinkBar links={links} />}
      <TitleWrapper>
        <Title data-testid="web-aggregation-title">{t('webAggregation.title')}</Title>
      </TitleWrapper>
      <InfoDownloadBox>
        <Typography
          data-testid="web-aggregation-survey-id"
          sx={{ fontSize: '16px', paddingTop: '10px' }}
        >{`ID: ${surveyId}`}</Typography>
        <ButtonContainer>
          <StyledButton
            data-testid="gt-download-button"
            startIcon={<DownloadIcon />}
            onClick={onClickGtDownload}
            disabled={!isTabulationExecuted}
          >
            {t('webAggregation.gt')}
          </StyledButton>
          <StyledButton
            data-testid="data-map-download-button"
            startIcon={<DownloadIcon />}
            onClick={onClickDataMapDownload}
            disabled={!isTabulationExecuted}
          >
            {t('webAggregation.dataMapRaw')}
          </StyledButton>
          <StyledButton
            data-testid="label-output-download-button"
            startIcon={<DownloadIcon />}
            onClick={onClickLabelOutputDownload}
            disabled={!isTabulationExecuted}
          >
            {t('webAggregation.labelOutput')}
          </StyledButton>
        </ButtonContainer>
      </InfoDownloadBox>
      <Typography
        data-testid="web-aggregation-survey-name"
        sx={{ fontSize: '24px', paddingTop: '20px' }}
      >
        {survey.name}
      </Typography>
      {Array.isArray(questionsList) &&
        questionsList.map((question, questionIdx) => (
          <div key={`questionType_${questionIdx}`}>
            {question.questionType === QUESTION_TYPE_ID.MULTIPLE_ANSWER && (
              <MultipleAnswer question={question} questionIdx={questionIdx} />
            )}
            {question.questionType === QUESTION_TYPE_ID.SINGLE_ANSWER && (
              <SingleAnswer question={question} questionIdx={questionIdx} />
            )}
            {question.questionType === QUESTION_TYPE_ID.FREE_ANSWER_NUMERIC && (
              <NumAnswer question={question} questionIdx={questionIdx} />
            )}
          </div>
        ))}

      {!isTabulationExecuted && (
        <ContentBox>
          <Typography>{t('webAggregation.tabulationNotExecuted')}</Typography>
        </ContentBox>
      )}
    </PageContent>
  );
};
export default WebAggregation;
